import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { parseISO, isToday, isPast, formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AddLeadDialog } from "../components/AddLeadDialog";
import {
  AlertCircle,
  AlertTriangle,
  Building,
  CalendarCheck,
  CheckCircle2,
  Hammer,
  MessageSquare,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { useAppStore } from "../store/useAppStore";

const panelShell = "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";

type WorkflowStep = {
  title: string;
  text: string;
  icon: typeof Sparkles;
  route: string;
  tone: string;
};

export default function Dashboard() {
  const { leads, opportunities, completeFollowUp } = useAppStore();
  const navigate = useNavigate();

  const allFollowUps = useMemo(
    () => leads.flatMap((lead) => lead.followUps.map((fu) => ({ ...fu, lead }))),
    [leads],
  );

  const pendingFollowUps = useMemo(
    () => allFollowUps.filter((fu) => !fu.completed).sort((a, b) => parseISO(a.dueDate).getTime() - parseISO(b.dueDate).getTime()),
    [allFollowUps],
  );

  const dueToday = useMemo(
    () => pendingFollowUps.filter((fu) => isToday(parseISO(fu.dueDate))),
    [pendingFollowUps],
  );

  const overdue = useMemo(
    () => pendingFollowUps.filter((fu) => !isToday(parseISO(fu.dueDate)) && isPast(parseISO(fu.dueDate))),
    [pendingFollowUps],
  );

  const newLeads = useMemo(() => leads.filter((l) => l.status === "New Lead"), [leads]);
  const hotLeads = useMemo(
    () => leads.filter((l) => l.status === "Follow-Up" || l.status === "Contacted"),
    [leads],
  );
  const underContract = useMemo(() => leads.filter((l) => l.status === "Under Contract"), [leads]);
  const recentOpps = useMemo(
    () => [...opportunities].sort((a, b) => parseISO(b.savedAt).getTime() - parseISO(a.savedAt).getTime()).slice(0, 5),
    [opportunities],
  );
  const latestOpp = recentOpps[0];
  const highOpps = useMemo(() => opportunities.filter((o) => o.opportunityScore >= 80), [opportunities]);

  const workflowSteps: WorkflowStep[] = [
    {
      title: "Discover",
      text: `${opportunities.length} live signals waiting to be reviewed.`,
      icon: Sparkles,
      route: "/opportunities",
      tone: "from-amber-500 to-orange-500",
    },
    {
      title: "Qualify",
      text: `${leads.length} leads in the inbox ready for triage.`,
      icon: Users,
      route: "/leads",
      tone: "from-sky-500 to-blue-500",
    },
    {
      title: "Follow up",
      text: `${overdue.length + dueToday.length} conversations need action today.`,
      icon: CalendarCheck,
      route: "/follow-ups",
      tone: "from-emerald-500 to-teal-500",
    },
    {
      title: "Close",
      text: `${underContract.length} deals are moving toward contract.`,
      icon: TrendingUp,
      route: "/pipeline",
      tone: "from-slate-700 to-slate-900",
    },
  ];

  const pipelinePulse =
    overdue.length > 0
      ? "Overdue items need attention first"
      : dueToday.length > 0
        ? "Today’s queue is active"
        : newLeads.length > 0
          ? "New leads are ready for first contact"
          : highOpps.length > 0
            ? "Signals are ready to review"
            : "Ready for new deals";

  const nextBestAction = useMemo(() => {
    if (overdue.length > 0) {
      const oldest = overdue[0];
      return {
        label: `Clear ${overdue.length} overdue follow-up${overdue.length === 1 ? "" : "s"}`,
        helper: `${oldest.lead.sellerName || "Unknown Seller"} • ${oldest.lead.propertyAddress}`,
        cta: "Open lead inbox",
        route: `/leads/${oldest.lead.id}`,
      };
    }

    if (dueToday.length > 0) {
      const next = dueToday[0];
      return {
        label: `Work ${dueToday.length} follow-up${dueToday.length === 1 ? "" : "s"} due today`,
        helper: `Start with ${next.lead.sellerName || "Unknown Seller"} at ${next.lead.propertyAddress}`,
        cta: "Open lead",
        route: `/leads/${next.lead.id}`,
      };
    }

    if (newLeads.length > 0) {
      const newest = newLeads[0];
      return {
        label: `Create the next touchpoint for ${newLeads.length} new lead${newLeads.length === 1 ? "" : "s"}`,
        helper: `Newest lead: ${newest.sellerName || "Unknown Seller"} • ${newest.propertyAddress}`,
        cta: "Review leads",
        route: "/leads",
      };
    }

    if (highOpps.length > 0) {
      const top = [...highOpps].sort((a, b) => b.opportunityScore - a.opportunityScore)[0];
      return {
        label: `Review ${highOpps.length} high-score signal${highOpps.length === 1 ? "" : "s"}`,
        helper: `${top.propertyAddress} is scoring ${top.opportunityScore}`,
        cta: "Open opportunities",
        route: "/opportunities",
      };
    }

    return {
      label: "Find the next distressed property",
      helper: "Run Deal Finder to populate signals, then turn them into leads.",
      cta: "Open Deal Finder",
      route: "/opportunities",
    };
  }, [overdue, dueToday, newLeads, highOpps]);

  const followUpWorkload = dueToday.length + overdue.length;
  const isQuietBoard = leads.length === 0 && opportunities.length === 0;
  const queueStatusLabel =
    overdue.length > 0
      ? `${overdue.length} overdue`
      : dueToday.length > 0
        ? `${dueToday.length} due today`
        : "No follow-ups due";
  const summaryMax = Math.max(leads.length, opportunities.length, followUpWorkload, underContract.length, 1);

  type SummaryCard = {
    label: string;
    value: number;
    helper: string;
    icon: typeof Users;
    tone: "emerald" | "amber" | "rose" | "blue" | "slate";
    onClick?: () => void;
  };

  const summaryCards: SummaryCard[] = [
    {
      label: "Active pipeline",
      value: leads.length,
      helper: `${hotLeads.length} hot • ${underContract.length} under contract`,
      icon: Users,
      tone: "emerald",
    },
    {
      label: "Signals found",
      value: opportunities.length,
      helper: `${highOpps.length} scored 80+`,
      icon: Sparkles,
      tone: "amber",
      onClick: () => navigate("/opportunities"),
    },
    {
      label: "Needs action",
      value: followUpWorkload,
      helper: `${dueToday.length} due today • ${overdue.length} overdue`,
      icon: AlertCircle,
      tone: overdue.length > 0 ? "rose" : "blue",
    },
    {
      label: "Ready to close",
      value: underContract.length,
      helper: "Leads in contract status",
      icon: TrendingUp,
      tone: "slate",
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white px-5 py-6 shadow-sm shadow-slate-900/5 sm:px-8 lg:px-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Command center</p>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Daily Deal Board</h1>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                {pipelinePulse}
              </span>
            </div>
            <p className="max-w-2xl text-sm leading-6 text-slate-500">
              A structured workspace for moving from signal to lead to follow-up without losing the thread.
            </p>
          </div>

          <AddLeadDialog>
            <Button className="bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-slate-950/15 transition-colors hover:bg-slate-800">
              + New Lead
            </Button>
          </AddLeadDialog>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 text-xs font-medium text-slate-600">
          <span className="rounded-full bg-slate-100 px-3 py-1.5">{leads.length} active leads</span>
          <span className="rounded-full bg-slate-100 px-3 py-1.5">{opportunities.length} market signals</span>
          <span className="rounded-full bg-slate-100 px-3 py-1.5">{followUpWorkload} follow-ups in queue</span>
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">{highOpps.length} high-priority signals</span>
        </div>
      </div>

      <div className={panelShell}>
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/80 px-5 py-4">
          <div>
            <h2 className="font-semibold text-slate-900">How the workspace flows</h2>
            <p className="text-sm text-slate-500">A simple path from discovery to conversion.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/opportunities")} className="text-xs text-slate-500 hover:text-slate-900">
            Start with signals
          </Button>
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-4">
          {workflowSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <button
                key={step.title}
                onClick={() => navigate(step.route)}
                className="group rounded-2xl border border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${step.tone} text-white shadow-sm`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                    0{index + 1}
                  </div>
                </div>
                <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                <p className="mt-1.5 text-sm leading-6 text-slate-500">{step.text}</p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 group-hover:text-slate-600">
                  Open {step.title.toLowerCase()}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const toneClasses =
            card.tone === "emerald"
              ? "bg-emerald-100 text-emerald-600"
              : card.tone === "amber"
                ? "bg-amber-100 text-amber-600"
                : card.tone === "rose"
                  ? "bg-rose-100 text-rose-600"
                  : "bg-slate-100 text-slate-600";

          const cardBody = (
            <CardContent className="flex items-center gap-3 p-4">
              <div className={`flex h-11 w-11 items-center justify-center rounded-full ${toneClasses}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-slate-400">{card.label}</p>
                <p className="text-2xl font-bold tracking-tight text-slate-900">{card.value}</p>
                <p className="text-xs text-slate-500">{card.helper}</p>
                <div className="mt-2 h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${
                      card.tone === "emerald"
                        ? "bg-emerald-500"
                        : card.tone === "amber"
                          ? "bg-amber-500"
                          : card.tone === "rose"
                            ? "bg-rose-500"
                            : "bg-slate-400"
                    }`}
                    style={{ width: `${Math.max(18, (card.value / summaryMax) * 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          );

          return card.onClick ? (
            <Card
              key={card.label}
              className="border-slate-200 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50/40"
              onClick={card.onClick}
            >
              {cardBody}
            </Card>
          ) : (
            <Card key={card.label} className="border-slate-200 shadow-sm">
              {cardBody}
            </Card>
          );
        })}
      </div>

      <Card className="border border-slate-200 bg-gradient-to-r from-slate-950 to-slate-800 text-white shadow-sm">
        <CardContent className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Next best action</p>
            <h2 className="text-lg font-semibold">{nextBestAction.label}</h2>
            <p className="text-sm text-slate-300">{nextBestAction.helper}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={() => navigate(nextBestAction.route)}
              className="bg-white text-slate-950 hover:bg-slate-100"
            >
              {nextBestAction.cta}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/leads")}
              className="border-white/15 bg-white/5 text-white shadow-sm hover:bg-white/10"
            >
              Open lead inbox
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-8 xl:grid-cols-[1fr_380px] items-start">
        <div className="flex flex-col gap-8">
          <div className={panelShell}>
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
              <div>
                <h2 className="flex items-center gap-2 font-bold text-slate-800">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Today’s Deal Intelligence
                </h2>
                <p className="text-xs text-slate-500">Recent public-signal activity, ranked by urgency</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/opportunities")}>View all</Button>
            </div>
            <div className="grid gap-3 border-b border-slate-100 bg-white px-5 py-4 sm:grid-cols-3">
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Signals shown</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{recentOpps.length} latest items</p>
              </div>
              <div className="rounded-lg bg-emerald-50 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">High score</p>
                <p className="mt-1 text-sm font-semibold text-emerald-900">{highOpps.length} at 80+</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Newest signal</p>
                <p className="mt-1 truncate text-sm font-semibold text-amber-900">{latestOpp ? formatDistanceToNow(parseISO(latestOpp.savedAt), { addSuffix: true }) : "None yet"}</p>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {recentOpps.length > 0 ? (
                recentOpps.map((opp) => (
                  <div key={opp.id} className="group flex items-center justify-between gap-4 p-5 transition-colors hover:bg-slate-50/60">
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                            opp.opportunityScore >= 80
                              ? "bg-emerald-100 text-emerald-700"
                              : opp.opportunityScore >= 60
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          Score: {opp.opportunityScore}
                        </span>
                        <span className="text-xs font-medium text-slate-400">
                          Added {formatDistanceToNow(parseISO(opp.savedAt), { addSuffix: true })}
                        </span>
                      </div>
                      <h3 className="truncate font-semibold text-slate-900 group-hover:text-amber-600">{opp.propertyAddress}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{opp.notes || "No notes attached yet."}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/opportunities")}
                      className="hidden whitespace-nowrap border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700 sm:flex"
                    >
                      View snapshot
                    </Button>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                  <Building className="mb-3 h-10 w-10 text-slate-200" />
                  <p className="font-medium text-slate-700">No civic signals found yet</p>
                  <p className="mt-1 mb-4 max-w-sm text-sm text-slate-500">
                    Use Deal Finder to scan your market for 311 complaints, code violations, and other distress clues.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/opportunities")}
                    className="gap-2 rounded-2xl border-emerald-200 bg-white px-4 py-3 shadow-sm hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    <Sparkles className="h-4 w-4 text-emerald-500" />
                    <span className="flex flex-col items-start leading-tight text-left">
                      <span className="font-semibold text-slate-800">Open Civic Signals</span>
                      <span className="text-[11px] text-slate-500">See the latest public distress clues</span>
                    </span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Lead workflow snapshot</p>
                  <p className="text-sm text-slate-500">Signals feed leads, leads feed follow-ups, and follow-ups feed conversions.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} className="text-xs text-slate-500 hover:text-blue-600">
                  View all pipeline activity
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
                  <MessageSquare className="h-3.5 w-3.5" />
                  {newLeads.length} new lead{newLeads.length === 1 ? "" : "s"}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {followUpWorkload} follow-ups waiting
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">
                  <TrendingUp className="h-3.5 w-3.5" />
                  {underContract.length} under contract
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card className="rounded-xl overflow-hidden border-slate-200 shadow-sm flex flex-col">
            <div className="relative z-10 flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
              <div>
                <h2 className="flex items-center gap-2 font-bold text-slate-800">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                  Follow-Ups Due
                </h2>
                <p className="text-xs text-slate-500">Priority queue for due and overdue touchpoints</p>
              </div>
              <div className="text-right">
                <span className="block text-xs font-bold text-slate-500">{followUpWorkload} remaining</span>
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">{queueStatusLabel}</span>
              </div>
            </div>
            <div className="max-h-[400px] divide-y divide-slate-100 overflow-y-auto bg-white">
              {[...overdue, ...dueToday].length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <CheckCircle2 className="mb-3 h-10 w-10 text-emerald-400" />
                  <p className="text-sm font-semibold text-slate-700">Queue clear</p>
                  <p className="mt-1 text-xs text-slate-400">No follow-ups due right now. New items will appear here automatically.</p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/leads")} className="mt-4 border-slate-200 text-slate-700 hover:bg-slate-50">
                    Review leads
                  </Button>
                </div>
              ) : (
                [...overdue, ...dueToday].slice(0, 5).map((fu) => (
                  <div key={fu.id} className="flex items-start justify-between gap-4 p-4 transition-colors hover:bg-slate-50">
                    <div className="min-w-0 flex-1 pr-2">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="truncate font-bold text-slate-800">{fu.lead.sellerName || "Unknown Seller"}</span>
                        {isPast(parseISO(fu.dueDate)) && !isToday(parseISO(fu.dueDate)) && (
                          <span className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700">
                            Late
                          </span>
                        )}
                      </div>
                      <p className="mb-2 truncate text-xs text-slate-500">{fu.lead.propertyAddress}</p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/leads/${fu.lead.id}`)}
                          className="h-7 border-blue-200 bg-blue-50 text-[10px] font-bold uppercase text-blue-600 hover:bg-blue-100"
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/leads/${fu.lead.id}`)}
                          className="h-7 border-emerald-200 bg-emerald-50 text-[10px] font-bold uppercase text-emerald-600 hover:bg-emerald-100"
                        >
                          Message
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => completeFollowUp(fu.lead.id, fu.id)}
                      className="shrink-0 rounded-full text-slate-300 hover:bg-emerald-50 hover:text-emerald-500"
                    >
                      <CheckCircle2 className="h-5 w-5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card className="rounded-xl overflow-hidden border-slate-200 shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-bold text-slate-800">
                  <Hammer className="h-4 w-4 text-slate-500" />
                  Suggested Actions
                </h2>
                <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Prioritized</span>
              </div>
            </div>
            <div className="flex flex-col gap-3 bg-white p-4">
              <Button
                variant="outline"
                className="flex h-auto justify-start gap-3 border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => navigate(nextBestAction.route)}
              >
                <div className="shrink-0 rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-white">
                  01
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{nextBestAction.label}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{nextBestAction.helper}</p>
                </div>
              </Button>

              <Button
                variant="outline"
                className="flex h-auto justify-start gap-3 border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => navigate("/opportunities")}
              >
                <div className="shrink-0 rounded-lg bg-amber-100 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-amber-700">
                  02
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">Review {highOpps.length} high-score signals</p>
                  <p className="mt-0.5 text-xs text-slate-500">Properties most likely to convert into opportunities</p>
                </div>
              </Button>

              <Button
                variant="outline"
                className="flex h-auto justify-start gap-3 border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => navigate("/map")}
              >
                <div className="shrink-0 rounded-lg bg-blue-100 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-blue-700">
                  03
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">Find nearby deals</p>
                  <p className="mt-0.5 text-xs text-slate-500">Stack opportunities on the map and cluster by area</p>
                </div>
              </Button>

              <Button
                variant="outline"
                className="flex h-auto justify-start gap-3 border-slate-200 px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => navigate("/leads")}
              >
                <div className="shrink-0 rounded-lg bg-emerald-100 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                  04
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">Message new leads</p>
                  <p className="mt-0.5 text-xs text-slate-500">{newLeads.length} new leads waiting for first contact</p>
                </div>
              </Button>
            </div>
          </Card>

          {isQuietBoard && (
            <Card className="border-dashed border-slate-200 bg-slate-50 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-white p-2 text-slate-500 shadow-sm">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">New here?</p>
                    <p className="mt-1 text-sm text-slate-500">
                      Start with a lead import or open Deal Finder to seed the command center with live signals.
                    </p>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button onClick={() => navigate("/opportunities")} className="bg-emerald-600 text-white hover:bg-emerald-700">
                        Open Deal Finder
                      </Button>
                      <AddLeadDialog>
                        <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-white">
                          Add Lead
                        </Button>
                      </AddLeadDialog>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
